
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { GroupCard } from "./GroupCard";
import { Group } from "@/types/group";
import { Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface GroupListProps {
  groups: Group[];
  onGroupClick: (groupId: string) => void;
  loading?: boolean;
  onRefresh?: () => void;
  error?: boolean;
}

// Create a skeleton loader card for better UX during loading
const GroupCardSkeleton = () => (
  <div className="p-6 space-y-4 rounded-lg border backdrop-blur-sm bg-gradient-to-br from-primary/5 to-green/5 border-primary/10">
    <div className="flex items-center space-x-4">
      <Skeleton className="h-12 w-12 rounded-full bg-gradient-to-r from-primary/10 to-green/10" />
      <div className="space-y-2 flex-1">
        <Skeleton className="h-5 w-4/5 bg-gradient-to-r from-primary/10 to-green/10" />
        <Skeleton className="h-4 w-3/5 bg-gradient-to-r from-primary/10 to-green/10" />
      </div>
      <Skeleton className="h-6 w-6 rounded bg-gradient-to-r from-primary/10 to-green/10" />
    </div>
    <div className="flex justify-between items-center">
      <div className="flex -space-x-2">
        <Skeleton className="h-8 w-8 rounded-full bg-gradient-to-r from-primary/10 to-green/10" />
        <Skeleton className="h-8 w-8 rounded-full bg-gradient-to-r from-primary/10 to-green/10" />
        <Skeleton className="h-8 w-8 rounded-full bg-gradient-to-r from-primary/10 to-green/10" />
      </div>
      <Skeleton className="h-4 w-16 bg-gradient-to-r from-primary/10 to-green/10" />
    </div>
  </div>
);

export const GroupList = memo(({ 
  groups, 
  onGroupClick, 
  loading = false,
  onRefresh,
  error = false
}: GroupListProps) => {
  const { t } = useTranslation();

  // Loading state with skeleton loaders for a better UX
  if (loading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <Skeleton className="h-6 w-1/3 bg-gradient-to-r from-primary/10 to-green/10" />
          <Skeleton className="h-8 w-20 bg-gradient-to-r from-primary/10 to-green/10" />
        </div>
        <div className="grid grid-cols-1 gap-3">
          {[1, 2, 3].map((i) => (
            <GroupCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Error state with retry button
  if (error) {
    return (
      <div className="text-center py-8 px-4 border border-dashed border-destructive rounded-lg bg-destructive/5 backdrop-blur-sm">
        <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-3" />
        <p className="text-lg font-medium text-destructive mb-2">
          {t('common.error', 'Error')}
        </p>
        <p className="text-muted-foreground mb-4">
          {t('groups.loadError', 'Could not load your groups. Please check your connection and try again.')}
        </p>
        {onRefresh && (
          <Button 
            onClick={onRefresh} 
            variant="outline" 
            size="sm"
            className="border-destructive text-destructive hover:bg-destructive/10"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.retry', 'Retry')}
          </Button>
        )}
      </div>
    );
  }

  // Validation check for groups array
  if (!Array.isArray(groups)) {
    console.error("Groups is not an array:", groups);
    return (
      <div className="text-center py-8 px-4 border border-dashed rounded-lg bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
        <p className="text-lg font-medium text-destructive mb-2">{t('common.error', 'Error')}</p>
        <p className="text-muted-foreground mb-4">{t('groups.dataError', 'There was a problem with your group data.')}</p>
        {onRefresh && (
          <Button onClick={onRefresh} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.retry', 'Retry')}
          </Button>
        )}
      </div>
    );
  }

  // Empty state
  if (groups.length === 0) {
    return (
      <div className="text-center py-8 px-4 border border-dashed rounded-lg bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
        <p className="text-lg font-medium mb-2">{t('groups.noGroupsYet', 'No Groups Yet')}</p>
        <p className="text-muted-foreground">{t('groups.createGroupPrompt', 'Create your first group to get started!')}</p>
      </div>
    );
  }

  // Normal render with groups
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('groups.yourActiveGroups', 'Your Active Groups')}</h2>
        {onRefresh && (
          <Button 
            onClick={onRefresh} 
            variant="ghost" 
            size="sm"
            className="text-xs flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" />
            {t('common.refresh', 'Refresh')}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3">
        {groups.map((group) => (
          <GroupCard
            key={group.id}
            group={group}
            onClick={() => onGroupClick(group.id)}
          />
        ))}
      </div>
    </div>
  );
});

GroupList.displayName = 'GroupList';
